import { useState } from 'react'

import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { APIErrorHandler } from '@percent/admin-dashboard/common/library/APIErrorHandler'

export type RemoveAdminModalState = 'success' | 'error'

export interface UseRemoveAdminModalReturn {
  selectedAccount: any | null
  isModalOpen: boolean
  modalState: RemoveAdminModalState | undefined
  errorMessage: string
  isLoading: boolean
  openModal: (account: any) => void
  closeModal: () => void
  confirmRemoveAdmin: () => Promise<void>
}

export function useRemoveAdminModal(): UseRemoveAdminModalReturn {
  const { accountService } = useServices()
  const [selectedAccount, setSelectedAccount] = useState<any | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalState, setModalState] = useState<RemoveAdminModalState | undefined>(undefined)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const [updateAdminStatusState, { mutateAsync: updateAdminStatus }] = useMutation(
    accountService.updateAdminStatus,
    () => {
      setModalState('success')
    },
    (error: any) => {
      setModalState('error')
      setErrorMessage(APIErrorHandler(error?.response?.data?.error))
    }
  )

  const openModal = (account: any) => {
    setSelectedAccount(account)
    setModalState(undefined)
    setErrorMessage('')
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedAccount(null)
    setModalState(undefined)
    setErrorMessage('')
  }

  const confirmRemoveAdmin = async () => {
    if (selectedAccount) {
      try {
        await updateAdminStatus({ accountId: selectedAccount.id, admin: false })
      } catch (error) {
        // Error is handled by the mutation's onError callback
      }
    }
  }

  return {
    selectedAccount,
    isModalOpen,
    modalState,
    errorMessage,
    isLoading: updateAdminStatusState.isPending,
    openModal,
    closeModal,
    confirmRemoveAdmin
  }
}
